import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Loginpage from './Components/Loginpage';
import ForgotPassword from './Components/ForgotPassword';
import ResetPassword from './Components/ResetPassword';
import Home from './Components/Home';
import Clients from './Components/Clients';
import Users from './Components/Users';
import Profile from './Components/Profile';
import ClientProfile from './Components/ClientProfile';
import Requirments from './Components/Requirments';
import TLHome from './Components/TLHome';
import TeamLeadRecruiters from './pages/TeamLeadRecruiters';
import TeamLeadClients from './pages/TeamLeadClients';
import { isValidSession, getUserType } from './utils/authStorage';
import AppLayout from './layout/AppLayout';
import RedirectOpenModal from './layout/RedirectOpenModal';
import RoleDashboard from './pages/RoleDashboard';
import RequirementsHub from './pages/RequirementsHub';
import ClientsHub from './pages/ClientsHub';
import UsersHub from './pages/UsersHub';
import CandidatesHub from './pages/CandidatesHub';
import ReportsHub from './pages/ReportsHub';
import TodayWorkMonitor from './pages/TodayWorkMonitor';
import RecruiterTodayWorkPage from './pages/RecruiterTodayWorkPage';
import InterviewsSchedule from './pages/InterviewsSchedule';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminProfilesViewPage from './pages/AdminProfilesViewPage';
import TeamLeadProfilesViewPage from './pages/TeamLeadProfilesViewPage';
import EmployeeWorkSummaryPage from './pages/EmployeeWorkSummaryPage';
import RecruiterTlRequirementDetailsPage from './pages/RecruiterTlRequirementDetailsPage';
import TrackingDashboard from './pages/TrackingDashboard';
import SettingsHub from './pages/SettingsHub';
import ChangePassword from './pages/ChangePassword';
import MySourcedProfilesPage from './pages/MySourcedProfilesPage';

function ProtectedLayout() {
  if (!isValidSession()) {
    return <Navigate to="/" replace />;
  }
  return <AppLayout />;
}

function RoleRoute({ allowed, children }) {
  const userType = getUserType();
  if (!allowed.includes(userType)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function LegacyHomeRedirect() {
  const userType = getUserType();
  if (userType === 'Admin') return <Navigate to="/dashboard" replace />;
  if (userType === 'TeamLead') return <Navigate to="/TLHome" replace />;
  return <Navigate to="/home" replace />;
}

function UserActionRedirect() {
  const { id } = useParams();
  const userType = getUserType();
  const listPath = userType === 'TeamLead' ? '/TLHome' : '/home?tab=claimed';
  return <RedirectOpenModal listPath={listPath} modalKey="upload-candidate" payload={{ reqId: id }} />;
}

function EditUserRedirect() {
  const { id } = useParams();
  return <RedirectOpenModal listPath="/users/list" modalKey="edit-user" payload={{ id }} />;
}

function EditClientRedirect() {
  const { id } = useParams();
  return <RedirectOpenModal listPath="/clients/list" modalKey="edit-client" payload={{ id }} />;
}

function App() {
  useLocation(); // Re-render on navigation so auth/session values refresh.

  const userType = getUserType();
  const sessionValid = isValidSession();

  const isAdmin = userType === 'Admin';
  const isTeamLead = userType === 'TeamLead';
  const isUser = userType === 'User';

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={sessionValid ? <LegacyHomeRedirect /> : <Loginpage />}
        />

        <Route element={<ProtectedLayout />}>
          <Route path="/dashboard" element={<RoleDashboard />} />
          <Route path="/requirements" element={<RequirementsHub />} />
          <Route path="/clients" element={<ClientsHub />} />
          <Route path="/clients/list" element={isAdmin ? <Clients /> : <Navigate to="/dashboard" replace />} />
          <Route path="/users" element={<RoleRoute allowed={['Admin']}><UsersHub /></RoleRoute>} />
          <Route path="/users/list" element={<RoleRoute allowed={['Admin']}><Users /></RoleRoute>} />
          <Route path="/candidates" element={isTeamLead ? <Navigate to="/team/profiles-view" replace /> : <CandidatesHub />} />
          <Route path="/reports" element={<ReportsHub />} />
          <Route path="/reports/analytics" element={<RoleRoute allowed={['Admin']}><AdminAnalytics /></RoleRoute>} />
          <Route path="/admin/profiles-view" element={<RoleRoute allowed={['Admin']}><AdminProfilesViewPage /></RoleRoute>} />
          <Route path="/team/profiles-view" element={<RoleRoute allowed={['TeamLead']}><TeamLeadProfilesViewPage /></RoleRoute>} />
          <Route path="/admin/employee-work-summary" element={<RoleRoute allowed={['Admin']}><EmployeeWorkSummaryPage /></RoleRoute>} />
          <Route path="/admin/recruiter-tl-requirement-details" element={<RoleRoute allowed={['Admin']}><RecruiterTlRequirementDetailsPage /></RoleRoute>} />
          <Route path="/reports/tracking" element={<RoleRoute allowed={['Admin', 'TeamLead']}><TrackingDashboard /></RoleRoute>} />
          <Route path="/work/today" element={<RoleRoute allowed={['TeamLead']}><TodayWorkMonitor /></RoleRoute>} />
          <Route path="/work/my-today" element={<RoleRoute allowed={['User']}><RecruiterTodayWorkPage /></RoleRoute>} />
          <Route path="/work/interviews" element={<RoleRoute allowed={['Admin', 'TeamLead', 'User']}><InterviewsSchedule /></RoleRoute>} />
          <Route path="/work/my-sourced-profiles" element={<RoleRoute allowed={['TeamLead', 'User']}><MySourcedProfilesPage /></RoleRoute>} />
          <Route path="/settings" element={<SettingsHub />} />
          <Route path="/settings/change-password" element={<ChangePassword />} />

          <Route path="/home" element={isUser ? <Home /> : <Navigate to="/dashboard" replace />} />
          <Route path="/TLHome" element={isTeamLead ? <TLHome view="requirements" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/team/requirements" element={isTeamLead ? <TLHome view="all-requirements" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/team/assign-requirements" element={isTeamLead ? <TLHome view="assign" /> : <Navigate to="/dashboard" replace />} />
          <Route
            path="/team/profiles"
            element={(
              <Navigate to={isTeamLead ? '/team/profiles-view' : '/dashboard'} replace />
            )}
          />
          <Route path="/team/recruiters" element={isTeamLead ? <TeamLeadRecruiters /> : <Navigate to="/dashboard" replace />} />
          <Route path="/team/clients" element={isTeamLead ? <TeamLeadClients /> : <Navigate to="/dashboard" replace />} />

          <Route path="/Adminhome" element={<Navigate to="/dashboard" replace />} />
          <Route path="/Adminaddnewrequirements" element={isAdmin ? <RedirectOpenModal listPath="/Requirments" modalKey="create-requirement" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/newrequirements" element={<Navigate to="/requirements" replace />} />
          <Route path="/AdminCreateUser" element={isAdmin ? <RedirectOpenModal listPath="/users/list" modalKey="create-user" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/CreateClient" element={isAdmin ? <RedirectOpenModal listPath="/clients/list" modalKey="create-client" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/Profile" element={<Profile />} />
          <Route path="/Clients" element={<Navigate to="/clients/list" replace />} />
          <Route path="/Users" element={<Navigate to="/users/list" replace />} />
          <Route path="/ClientProfile/:id" element={<ClientProfile />} />
          <Route path="/UptadeClient/:id" element={isAdmin ? <EditClientRedirect /> : <Navigate to="/dashboard" replace />} />
          <Route path="/UpdateUser/:id" element={isAdmin ? <EditUserRedirect /> : <Navigate to="/dashboard" replace />} />
          <Route path="/Requirments/mine" element={isAdmin ? <Requirments scope="mine" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/Requirments" element={isAdmin ? <Requirments /> : isTeamLead ? <TLHome view="requirements" /> : <Navigate to="/dashboard" replace />} />
          <Route path="/UserAction/:id/:user" element={<UserActionRedirect />} />
          <Route path="/TeamClient" element={<Navigate to="/team/clients" replace />} />
          <Route path="/TLNewReq" element={isTeamLead ? <RedirectOpenModal listPath="/TLHome" modalKey="create-tl-requirement" /> : <Navigate to="/dashboard" replace />} />

          <Route path="/AdminTopNav" element={<Navigate to="/dashboard" replace />} />
          <Route path="/UserTopNav" element={<Navigate to="/dashboard" replace />} />
          <Route path="/TeamLeadTopNav" element={<Navigate to="/dashboard" replace />} />
        </Route>

        <Route path="/forgotpassword" element={sessionValid ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
        <Route path="/ResetPassword/:id/:token" element={<ResetPassword />} />
        <Route path="*" element={<div className="hr-page-content">Page Not Found</div>} />
      </Routes>
    </div>
  );
}

export default App;